import { Injectable } from '@angular/core';
import {
  CropFieldNutrientCalculations,
  Nutrient,
  NutrientCalculations,
  NutrientColor,
  NutrientKey,
  NutrientSubKeys,
  SampleTakingMoment,
} from 'src/app/bem/shared/models/models';

@Injectable({
  providedIn: 'root',
})
export class BemCropFieldSortingService {
  adviceMappings: {
    [K in NutrientKey]: K extends keyof NutrientSubKeys
    ? Record<NutrientSubKeys[K], NutrientColor> : null
  } = {
      k: { k1: 'yellow', k4: 'yellow', k12: 'yellow' },
      mn: { mn1: 'red', mn3: 'red', mn5: 'red', mn6: 'red', mn7: 'red', mn2: 'green', mn4: 'green', mn8: 'green' },
      b: { b1: 'red', b2: 'green' },
      mg: { mg1: 'green', mg2: 'yellow', mg3: 'red', mg4: 'red' },
      s: { s1: 'red', s2: 'yellow', s3: 'green' },
      n: null,
    };

  constructor() { }

  getColorFromAdvice(nutrient: Nutrient | undefined, nutrientKey: NutrientKey): string | undefined {
    return nutrient?.adviceTxt
      ?.map((a): string | undefined => {
        const colorMapping = this.adviceMappings[nutrientKey];
        if (colorMapping && typeof a === 'string') {
          return colorMapping[a.toLowerCase() as keyof typeof colorMapping];
        }
        return undefined;
      })
      .find((color) => color);
  }

  getColorFromRecKgha(nutrient: Nutrient | undefined): string | undefined {
    if (nutrient?.recKgha === undefined || nutrient?.recKgha === null) {
      return undefined;
    }

    if (nutrient?.recKgha > 0) {
      return 'red';
    }
    if (nutrient?.recKgha === 0) {
      return 'green';
    }
    return undefined;
  }

  getNutrientColor(nutrientCalculations: NutrientCalculations, n: SampleTakingMoment): NutrientCalculations {
    if (!nutrientCalculations) {
      return nutrientCalculations;
    }

    const updatedCalculations = { ...nutrientCalculations };

    (Object.keys(updatedCalculations) as NutrientKey[]).forEach((key) => {
      const nutrient = updatedCalculations[key];
      if (!nutrient) {
        return;
      }

      const color = this.getColorFromAdvice(nutrient, key) ?? this.getColorFromRecKgha(nutrient);

      updatedCalculations[key] = {
        ...nutrient,
        color,
      };

      if (key === 'mg' && !color) {
        if (n === 1) {
          const recKgha = (nutrient as Nutrient)?.recKgha;
          if (recKgha !== undefined && recKgha !== null) {
            updatedCalculations[key] = {
              ...nutrient,
              color: recKgha > 0 ? 'red' : 'green',
            };
          }
        }
      }
    });

    return updatedCalculations;
  }

  sortFields(fields: CropFieldNutrientCalculations[]): CropFieldNutrientCalculations[] {
    return fields.map((field) => {
      const nutrientCalculation = this.getNutrientColor(field.nutrientCalculation, field.n);
      return { ...field, nutrientCalculation };
    }).sort((a, b) => {
      const nameA = typeof a.name === 'string' ? a.name : '';
      const nameB = typeof b.name === 'string' ? b.name : '';
      return nameA.localeCompare(nameB);
    });
  }
}
